import React from 'react';
import {Platform, View} from 'react-native';
import {format, startOfToday, subMonths} from 'date-fns';
import {isNull} from 'lodash';

import Card from '/component/base/Card';
import Delta from '/component/base/Delta';
import FormattedNumber from '/component/base/FormattedNumber';
import InlineFormattedNumber from '/component/base/FormattedNumber/InlineFormattedNumber';
import Icon from '/component/base/Icon';
import ListItem from '/component/base/ListItem';
import Text from '/component/base/Text';
import TouchableOpacity from '/component/base/TouchableOpacity';
import {AccessibilityWrapper} from '/component/layout/AccessibilityWrapper';
import {Cards} from '/constant/TestIDs';
import {withTheme} from '/provider/ThemeProvider';
import {withTranslation} from '/provider/TranslationProvider';
import {generateComponentCallback} from '/util/component.util';
import testProperties from '/util/testing/testProperties';

import {sdlmTransformer} from '../../../Teams/TeamsVolumeSummaryCard/TeamsVolumeSummaryCard.util';

import {useDashboardMetricCard} from './DashboardMetricCard.hook';
import {styles} from './DashboardMetricCard.style';
import {DashboardMetricCardProps} from './DashboardMetricCard.types';

export const DashboardMetricCard = ({
  analyticsHeading = '',
  tt = (key: string, defaultValue: React.ReactNode = '') =>
    String(defaultValue),
  getTheme = (key: string) => key,
  onShowInfoPopover = () => {},
  onShowReport = () => {},
  heading = '',
  current = null,
  projectedThisMonth = null,
  thisDayLastMonth = null,
  totalLastMonth = null,
  delta = null,
  infoPopOverHeading = '',
  infoPopOverBody = '',
  testID = null,
  lastPaidRank = '',
  style = {},
}: DashboardMetricCardProps) => {
  const {onInfoPress, onCardPress} = useDashboardMetricCard({
    analyticsHeading,
    infoPopOverHeading,
    infoPopOverBody,
    onShowInfoPopover,
    onShowReport,
    lastPaidRank,
  });

  return (
    <AccessibilityWrapper accessible={false}>
      <Card
        isDashboard
        testID={testID}
        fullWidth
        style={{...styles.container, ...style}}
        onPress={() => onCardPress(analyticsHeading)}>
        <ListItem
          isDashboard
          label={
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              testID={Cards.cardTitle}
              variant="heading"
              accessible={true}
              accessibilityRole="header"
              accessibilityLabel={
                Platform.OS === 'android'
                  ? `${analyticsHeading}`
                  : `${analyticsHeading} Heading .`
              }>
              {heading}
            </Text>
          }
          readonly
          suffix={
            <TouchableOpacity
              onPress={onInfoPress}
              overflow={'hidden'}
              style={styles.infoButton}
              {...testProperties('info')}
              accessible={true}
              focusable={true}
              accessibilityLabel={`${analyticsHeading} information button`}>
              <Icon name="info" style={styles.infoIcon} />
            </TouchableOpacity>
          }
          style={{base: styles.headerListItem}}
        />

        <View style={styles.currentValue} accessible={true}>
          <FormattedNumber
            testID={Cards.cardMainValue}
            value={current}
            truncate
            style={{
              ...styles.currentValue,
              text: {
                ...styles.currentValueText,
                ...getTheme('competency.textColor'),
              },
            }}
          />
        </View>
        <ListItem
          value={projectedThisMonth}
          label={
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              testID={Cards.cardProjectedThisMonthTitle}
              accessible={true}
              accessibilityLabel={'Remaining Projected This Month'}>
              {tt('carousel.projected', 'Remaining Projected This Month')}
            </Text>
          }
          readonly
          suffix={
            <InlineFormattedNumber
              testID={Cards.cardProjectedThisMonthValue}
              truncate
              value={projectedThisMonth}
            />
          }
        />

        <View
          style={styles.divider}
          accessible={false}
          importantForAccessibility="no"
          accessibilityElementsHidden={true}
        />

        {!isNull(thisDayLastMonth) && (
          <>
            <ListItem
              value={sdlmTransformer(thisDayLastMonth)}
              style={{base: styles.listItem}}
              label={
                <Text
                  allowFontScaling
                  maxFontSizeMultiplier={2}
                  testID={Cards.cardSameLastMonthTitle}
                  accessible={true}>
                  {tt('carousel.sameDay', 'Same Day Last Month')}
                </Text>
              }
              readonly
              suffix={
                <>
                  <InlineFormattedNumber
                    style={styles.sdlmValue}
                    testID={Cards.cardSameLastMonthValue}
                    truncate
                    value={sdlmTransformer(thisDayLastMonth)}
                  />
                </>
              }
            />
            <Delta
              value={sdlmTransformer(delta)}
              style={{base: styles.delta}}
            />
          </>
        )}

        <ListItem
          value={sdlmTransformer(totalLastMonth)}
          style={{base: styles.listItem}}
          label={
            <Text
              allowFontScaling
              maxFontSizeMultiplier={2}
              testID={Cards.cardLastMonthTotalTitle}
              accessible={true}>
              {tt(
                'carousel.lastMonth',
                generateComponentCallback({
                  component: (
                    <>
                      Last Month ({format(subMonths(startOfToday(), 1), 'MMMM')}
                      ) Total
                    </>
                  ),
                }),
              )}
            </Text>
          }
          readonly
          suffix={
            <InlineFormattedNumber
              testID={Cards.cardLastMonthTotalValue}
              truncate
              value={sdlmTransformer(totalLastMonth)}
            />
          }
        />
      </Card>
    </AccessibilityWrapper>
  );
};

export default withTranslation('screen.dashboard.section.DashboardMetricCard')(
  withTheme(DashboardMetricCard),
);
